import React, { useEffect, useState } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { formatCurrency } from '../utils/currency';
import { Play } from 'lucide-react';

const OwnerStatements = () => {
    const [statements, setStatements] = useState([]);
    const [loading, setLoading] = useState(true);
    const [detail, setDetail] = useState(null);
    const [showForm, setShowForm] = useState(false);
    const [properties, setProperties] = useState([]);

    const load = async () => {
        setLoading(true);
        try {
            const [s, p] = await Promise.all([
                axios.get('/api/owner-statements'),
                axios.get('/api/properties').catch(() => ({ data: { properties: [] } })),
            ]);
            setStatements(s.data.statements || []);
            setProperties(p.data.properties || []);
        } catch { toast.error('Failed'); }
        finally { setLoading(false); }
    };
    useEffect(() => { load(); }, []);

    return (
        <div className="max-w-6xl mx-auto space-y-6">
            <div className="flex justify-between items-center">
                <h1 className="text-2xl font-bold">Owner statements</h1>
                <button onClick={() => setShowForm(true)} className="inline-flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg">
                    <Play className="h-4 w-4" /> Generate statement
                </button>
            </div>

            {loading ? <div>Loading…</div> : (
                <div className="bg-white rounded-xl border">
                    <table className="w-full text-sm">
                        <thead className="bg-gray-50 text-left">
                            <tr><th className="p-3">Owner</th><th className="p-3">Property</th><th className="p-3">Period</th>
                                <th className="p-3 text-right">Income</th><th className="p-3 text-right">Expenses</th>
                                <th className="p-3 text-right">Mgmt fee</th><th className="p-3 text-right">Disbursement</th>
                                <th className="p-3">Status</th></tr>
                        </thead>
                        <tbody>
                            {statements.length === 0 && <tr><td colSpan={8} className="p-6 text-center text-gray-500">No statements yet.</td></tr>}
                            {statements.map(s => (
                                <tr key={s.id} className="border-t hover:bg-gray-50 cursor-pointer" onClick={() => setDetail(s.id)}>
                                    <td className="p-3">{s.owner_name}</td>
                                    <td className="p-3">{s.property_name}</td>
                                    <td className="p-3">{s.period_start} → {s.period_end}</td>
                                    <td className="p-3 text-right">{formatCurrency(s.gross_income)}</td>
                                    <td className="p-3 text-right text-red-600">-{formatCurrency(s.total_expenses)}</td>
                                    <td className="p-3 text-right text-red-600">-{formatCurrency(s.management_fee)}</td>
                                    <td className="p-3 text-right font-semibold">{formatCurrency(s.owner_disbursement)}</td>
                                    <td className="p-3 capitalize">{s.status}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {showForm && <GenerateForm properties={properties} onClose={() => setShowForm(false)} onDone={() => { setShowForm(false); load(); }} />}
            {detail && <Detail id={detail} onClose={() => setDetail(null)} onChanged={load} />}
        </div>
    );
};

const GenerateForm = ({ properties, onClose, onDone }) => {
    const now = new Date();
    const prev = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const start = prev.toISOString().slice(0, 10);
    const end = new Date(now.getFullYear(), now.getMonth(), 0).toISOString().slice(0, 10);
    const [f, setF] = useState({ property_id:'', period_start: start, period_end: end });
    const [saving, setSaving] = useState(false);
    const submit = async e => {
        e.preventDefault(); setSaving(true);
        try {
            await axios.post('/api/owner-statements/generate', f);
            toast.success('Generated'); onDone();
        } catch (err) { toast.error(err.response?.data?.message || 'Failed'); setSaving(false); }
    };
    return (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
            <form onSubmit={submit} className="bg-white rounded-xl max-w-md w-full">
                <div className="p-4 border-b flex justify-between"><h2 className="font-semibold">Generate owner statement</h2><button type="button" onClick={onClose}>✕</button></div>
                <div className="p-4 space-y-3">
                    <select required className="w-full border rounded-lg px-3 py-2" value={f.property_id} onChange={e => setF({...f, property_id:e.target.value})}>
                        <option value="">Property…</option>
                        {properties.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                    </select>
                    <div className="grid grid-cols-2 gap-3">
                        <input type="date" required className="border rounded-lg px-3 py-2" value={f.period_start} onChange={e => setF({...f, period_start:e.target.value})} />
                        <input type="date" required className="border rounded-lg px-3 py-2" value={f.period_end} onChange={e => setF({...f, period_end:e.target.value})} />
                    </div>
                </div>
                <div className="p-4 border-t flex justify-end gap-2">
                    <button type="button" onClick={onClose} className="px-4 py-2 border rounded-lg">Cancel</button>
                    <button disabled={saving} className="px-4 py-2 bg-primary-600 text-white rounded-lg">{saving?'Generating…':'Generate'}</button>
                </div>
            </form>
        </div>
    );
};

const Detail = ({ id, onClose, onChanged }) => {
    const [data, setData] = useState(null);
    useEffect(() => {
        (async () => {
            try { const r = await axios.get(`/api/owner-statements/${id}`); setData(r.data); }
            catch { toast.error('Failed'); }
        })();
    }, [id]);
    const markPaid = async () => {
        try { await axios.post(`/api/owner-statements/${id}/mark-paid`); toast.success('Marked paid'); onChanged(); onClose(); }
        catch { toast.error('Failed'); }
    };
    if (!data) return null;
    const s = data.statement;
    return (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
            <div className="bg-white rounded-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
                <div className="p-4 border-b flex justify-between"><h2 className="font-semibold">Statement — {s.property_name}</h2><button onClick={onClose}>✕</button></div>
                <div className="p-6 space-y-4">
                    <div className="grid grid-cols-3 gap-4 text-sm">
                        <div><div className="text-gray-500">Owner</div><div>{s.owner_name}</div></div>
                        <div><div className="text-gray-500">Period</div><div>{s.period_start} → {s.period_end}</div></div>
                        <div><div className="text-gray-500">Status</div><div className="capitalize">{s.status}</div></div>
                        <div><div className="text-gray-500">Gross income</div><div>{formatCurrency(s.gross_income)}</div></div>
                        <div><div className="text-gray-500">Expenses</div><div>-{formatCurrency(s.total_expenses)}</div></div>
                        <div><div className="text-gray-500">Management fee</div><div>-{formatCurrency(s.management_fee)}</div></div>
                        <div><div className="text-gray-500">Ownership share</div><div>{s.ownership_share}%</div></div>
                        <div className="col-span-2"><div className="text-gray-500">Disbursement</div><div className="text-xl font-semibold">{formatCurrency(s.owner_disbursement)}</div></div>
                    </div>
                    <div>
                        <h3 className="font-semibold mb-2">Line items</h3>
                        <table className="w-full text-sm">
                            <thead className="bg-gray-50"><tr><th className="p-2 text-left">Date</th><th className="p-2 text-left">Type</th><th className="p-2 text-left">Description</th><th className="p-2 text-right">Amount</th></tr></thead>
                            <tbody>
                                {data.lines.map(l => (
                                    <tr key={l.id} className="border-t">
                                        <td className="p-2">{l.line_date}</td>
                                        <td className="p-2 capitalize">{l.line_type}</td>
                                        <td className="p-2">{l.description}</td>
                                        <td className={`p-2 text-right ${Number(l.amount)<0 ? 'text-red-600':'text-green-700'}`}>{formatCurrency(l.amount)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    {s.status !== 'paid' && (
                        <div className="text-right"><button onClick={markPaid} className="px-4 py-2 bg-primary-600 text-white rounded-lg">Mark as paid</button></div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default OwnerStatements;
